import { api } from "api"
import { enqueueSnackbar } from "notistack"
import { TagTypes } from "./commonTypes"

export interface DocHubFileEntry {
    file_type: string
    file_name: string[]
    doc_id?: number
}

export interface UploadDocumentsRequest {
    ticket_id: number
    user_id: number
    files: DocHubFileEntry[]
    is_site?: boolean
    is_sub_ticket?: boolean
    /** Saved on the document so it can be found by project — HOTO files need it. */
    project_id?: number
}

export interface DocHubUrlInfo {
    file_url: string
    presigned_url: string
    doc_id: number
    report_id: number | null
}

export interface DocHubUrlGroup {
    file_type: string
    urls: DocHubUrlInfo[]
}

export interface UploadDocumentsResponse {
    message: string
    urls: DocHubUrlGroup[]
}

export interface SubmitFileInfo {
    file_url: string
    doc_id: number
    report_id: number | null
}

export interface SubmitFinalUploadsRequest {
    ticket_id: number
    file_infos: SubmitFileInfo[]
    tags?: TagTypes[]
    is_site?: boolean
}

export interface FinalUrlEntry {
    file_url: string
}

export interface SubmitFinalUploadsResponse {
    final_urls: FinalUrlEntry[]
}

export interface EditDocumentsRequest {
    ticket_id: number
    user_id: number
    removed_ids: number[]
    files: DocHubFileEntry[]
    is_site?: boolean
    is_sub_ticket?: boolean
    project_id?: number
}

export interface EditDocumentsResponse {
    message: string
    urls: DocHubUrlGroup[]
}

export interface DeleteDocumentsRequest {
    ids: number[]
    tags?: TagTypes[]
}

export interface DeleteDocumentsResponse {
    message: string
}

const fileUploadService = api.injectEndpoints({
    endpoints: build => ({
        uploadDocuments: build.mutation<UploadDocumentsResponse, UploadDocumentsRequest>({
            query: body => ({
                url: `upload_documents`,
                useV3: true,
                method: "POST",
                body,
            }),
        }),

        submitFinalUploads: build.mutation<SubmitFinalUploadsResponse, any>({
            query: ({ tags, ...body }) => ({
                url: `submit_final_uploads`,
                useV3: true,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_, __, args) => args?.tags || [],
        }),

        editDocuments: build.mutation<EditDocumentsResponse, EditDocumentsRequest>({
            query: body => ({
                url: `edit_documents`,
                useV3: true,
                method: "PUT",
                body,
            }),
        }),

        deleteDocuments: build.mutation<DeleteDocumentsResponse, DeleteDocumentsRequest>({
            query: ({ tags, ...body }) => ({
                url: `delete_documents`,
                useV3: true,
                method: "DELETE",
                body,
            }),
            invalidatesTags: (_, __, args) => args?.tags || [],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.message || "Something went wrong!"}`, { variant: "error" })
                }
            },
        }),
    }),
})

export const {
    useUploadDocumentsMutation,
    useSubmitFinalUploadsMutation,
    useEditDocumentsMutation,
    useDeleteDocumentsMutation,
} = fileUploadService

export default fileUploadService
